import { apiClient } from "@/lib/apiClient";
import type {
  ApprovalStatus,
  DocumentDetail,
  FilterState,
  ListDocumentsResponse,
  ListTab,
  ListTabCounts,
  MotCandidate,
  ParcelSummary,
} from "@/types/domain";

export type { MotCandidate } from "@/types/domain";

export function getDocument(id: string): Promise<DocumentDetail> {
  return apiClient.get<DocumentDetail>(
    `/api/documents/${encodeURIComponent(id)}`,
  );
}

/** Translate the tab enum to backend query params for the flat doc list. */
export function tabToDocQuery(tab: ListTab): {
  approvalStatus?: string;
  categorization?: string;
  extractionFailed?: "true";
} {
  switch (tab) {
    case "all":
      return {};
    case "needsCategorization":
      return { categorization: "uncategorized" };
    case "needsReview":
      return { approvalStatus: "pending_review" };
    case "approved":
      return { approvalStatus: "approved" };
    case "archived":
      return { approvalStatus: "archived" };
    case "failed":
      return { extractionFailed: "true" };
  }
}

function addSharedDocParams(params: URLSearchParams, filters: FilterState): void {
  if (filters.subject) params.set("subject", filters.subject);
  if (filters.from) params.set("from", filters.from);
  if (filters.documentType) params.set("documentType", filters.documentType);
  if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
  if (filters.dateTo) params.set("dateTo", filters.dateTo);
  if (filters.tradeKey) params.set("tradeKey", filters.tradeKey);
  if (filters.correctedOnly) params.set("correctedOnly", "true");
}

function buildDocQuery(
  filters: FilterState,
  skipToken: string | null,
  top = 50,
): string {
  const params = new URLSearchParams();
  addSharedDocParams(params, filters);
  const tabQuery = tabToDocQuery(filters.listTab);
  if (tabQuery.approvalStatus) params.set("approvalStatus", tabQuery.approvalStatus);
  if (tabQuery.categorization) params.set("categorization", tabQuery.categorization);
  if (tabQuery.extractionFailed)
    params.set("extractionFailed", tabQuery.extractionFailed);
  params.set("sort", filters.sort);
  params.set("top", String(top));
  if (skipToken) params.set("skipToken", skipToken);
  return params.toString();
}

export function listDocuments(
  filters: FilterState,
  skipToken: string | null,
  top = 50,
  signal?: AbortSignal,
): Promise<ListDocumentsResponse> {
  return apiClient.get<ListDocumentsResponse>(
    `/api/documents?${buildDocQuery(filters, skipToken, top)}`,
    { signal },
  );
}

/** Per-tab document counts — shared filters apply but the tab axis is ignored. */
export function getDocumentCounts(filters: FilterState): Promise<ListTabCounts> {
  const params = new URLSearchParams();
  addSharedDocParams(params, filters);
  const q = params.toString();
  const url = q ? `/api/documents/counts?${q}` : "/api/documents/counts";
  return apiClient.get<ListTabCounts>(url);
}

export interface PatchDocumentResponse {
  documentId: string;
  tradeKey: string | null;
}

export function patchDocument(
  id: string,
  body: { tradeKey: string | null },
): Promise<PatchDocumentResponse> {
  return apiClient.patch<PatchDocumentResponse>(
    `/api/documents/${encodeURIComponent(id)}`,
    body,
  );
}

export interface LifecycleResponse {
  documentId: string;
  approvalStatus: ApprovalStatus;
  approvedBy: string | null;
}

export function archiveDocument(id: string): Promise<LifecycleResponse> {
  return apiClient.post<LifecycleResponse>(
    `/api/documents/${encodeURIComponent(id)}/archive`,
    {},
  );
}

export function removeDocument(id: string): Promise<LifecycleResponse> {
  return apiClient.post<LifecycleResponse>(
    `/api/documents/${encodeURIComponent(id)}/remove`,
    {},
  );
}

export function setSelectedParcelOnDocument(
  id: string,
  parcelNumber: number,
): Promise<DocumentDetail> {
  return apiClient.patch<DocumentDetail>(
    `/api/documents/${encodeURIComponent(id)}/selected-parcel`,
    { parcelNumber },
  );
}

/**
 * Refetch parcel candidates using the document's effective trade
 * (per-doc override if set, else inherited from the email). Use this
 * over ``refetchEmailParcels`` when the doc has an override but the
 * parent email does not — the email-level endpoint 409s in that case.
 */
export function refetchDocumentParcels(id: string): Promise<DocumentDetail> {
  return apiClient.post<DocumentDetail>(
    `/api/documents/${encodeURIComponent(id)}/parcel-fetch`,
    {},
  );
}

/** Fetch enriched parcel candidate metadata (trade/qty/dates) for the
 * document's effective trade, hydrated on demand from Symphony.
 */
export function getDocumentParcelCandidateDetails(
  id: string,
): Promise<ParcelSummary[]> {
  return apiClient.get<ParcelSummary[]>(
    `/api/documents/${encodeURIComponent(id)}/parcel-candidates/details`,
  );
}

export function lookupMotCandidates(
  vesselName: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<MotCandidate[]> {
  const params = new URLSearchParams({ vesselName });
  if (limit !== undefined) params.set("limit", String(limit));
  return apiClient.get<MotCandidate[]>(`/api/mot/lookup?${params.toString()}`, {
    signal,
  });
}

export function saveCorrections(
  id: string,
  correctedData: Record<string, unknown> | null,
): Promise<DocumentDetail> {
  return apiClient.patch<DocumentDetail>(
    `/api/documents/${encodeURIComponent(id)}/corrections`,
    { correctedData },
  );
}

/**
 * LRU cache of authenticated page image blob URLs. Capped at 200 entries
 * with oldest-first eviction; evicted URLs are revoked so the browser frees
 * the backing Blob.
 */
const MAX_BLOB_CACHE = 200;
const blobCache = new Map<string, string>();

export async function getPageBlob(
  documentId: string,
  pageName: string,
): Promise<string> {
  const key = `${documentId}/${pageName}`;
  const cached = blobCache.get(key);
  if (cached !== undefined) {
    // Refresh LRU order
    blobCache.delete(key);
    blobCache.set(key, cached);
    return cached;
  }
  const url = await apiClient.blob(
    `/api/documents/${encodeURIComponent(documentId)}/pages/${encodeURIComponent(pageName)}`,
  );
  blobCache.set(key, url);
  while (blobCache.size > MAX_BLOB_CACHE) {
    const oldest = blobCache.keys().next().value;
    if (oldest === undefined) break;
    const stale = blobCache.get(oldest);
    blobCache.delete(oldest);
    if (stale !== undefined) URL.revokeObjectURL(stale);
  }
  return url;
}
